import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  MESSAGE_FEEDBACK_REASONS,
  type MessageFeedbackReason,
} from "@sage-mcp/shared";
import {
  api,
  ApiError,
  type AdminConversationFacetMachine,
  type AdminConversationFacets,
  type AdminConversationFailureFilter,
  type AdminConversationFeedbackFilter,
  type AdminConversationRow,
} from "../api.js";
import { useToken } from "../auth.js";
import { CREDIT_REASON_LABELS } from "../credit-reason-labels.js";
import { FEEDBACK_REASON_LABELS } from "../feedback-labels.js";
import { Badge } from "../components/Badge.js";

const TIME_PRESETS: Array<{ label: string; minutes: number }> = [
  { label: "24h", minutes: 60 * 24 },
  { label: "7d", minutes: 60 * 24 * 7 },
  { label: "30d", minutes: 60 * 24 * 30 },
  { label: "90d", minutes: 60 * 24 * 90 },
];

const PAGE_SIZE = 100;

interface Filters {
  customerId: string | null;
  machineId: string | null;
  sinceMinutes: number;
  q: string;
  includeArchived: boolean;
  // null = no feedback filter at all (every conversation).
  feedback: AdminConversationFeedbackFilter | null;
  // Only meaningful alongside feedback === "flagged"; the API treats it as
  // implying that filter anyway.
  feedbackReason: MessageFeedbackReason | null;
  // null = no failure filter. Distinct from `feedback`: this is what actually
  // broke (a failed tool call, or a turn that never completed), not what the end
  // user reported — the two disagree often enough to be worth separate filters.
  failures: AdminConversationFailureFilter | null;
}

const DEFAULT_FILTERS: Filters = {
  customerId: null,
  machineId: null,
  sinceMinutes: 60 * 24 * 30,
  q: "",
  includeArchived: false,
  feedback: null,
  feedbackReason: null,
  failures: null,
};

const FAILURE_PRESETS: Array<{
  value: AdminConversationFailureFilter | null;
  label: MessageDescriptor;
}> = [
  { value: null, label: msg`All` },
  { value: "any", label: msg`With failures` },
  { value: "tool", label: msg`Tool error` },
  { value: "turn", label: msg`Turn error` },
  { value: "clean", label: msg`Clean` },
];

const FEEDBACK_PRESETS: Array<{
  value: AdminConversationFeedbackFilter | null;
  label: MessageDescriptor;
}> = [
  { value: null, label: msg`All` },
  { value: "flagged", label: msg`Flagged` },
  { value: "rated", label: msg`Rated` },
  { value: "unflagged", label: msg`Not flagged` },
];

export function Conversations() {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => filtersFromParams(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  const [rows, setRows] = useState<AdminConversationRow[]>([]);
  const [nextCursor, setNextCursor] = useState<{ updatedAt: string; id: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [facets, setFacets] = useState<AdminConversationFacets | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(h);
  }, [filters.q]);

  useEffect(() => {
    setSearchParams(paramsFromFilters({ ...filters, q: debouncedQ }), { replace: true });
  }, [filters, debouncedQ, setSearchParams]);

  const load = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const res = await api.listConversations(token, {
        customerId: filters.customerId ?? undefined,
        machineId: filters.machineId ?? undefined,
        sinceMinutes: filters.sinceMinutes,
        q: debouncedQ || undefined,
        includeArchived: filters.includeArchived,
        feedback: filters.feedback,
        feedbackReason: filters.feedbackReason,
        failures: filters.failures,
        limit: PAGE_SIZE,
      });
      setRows(res.conversations);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [
    token,
    filters.customerId,
    filters.machineId,
    filters.sinceMinutes,
    filters.includeArchived,
    filters.feedback,
    filters.feedbackReason,
    filters.failures,
    debouncedQ,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void api
      .conversationFacets(token)
      .then(setFacets)
      .catch(() =>
        setFacets({
          customers: [],
          machines: [],
          feedbackReasons: [],
          flaggedCount: 0,
          failureCounts: { any: 0, tool: 0, turn: 0 },
        }),
      );
  }, [token]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await api.listConversations(token, {
        customerId: filters.customerId ?? undefined,
        machineId: filters.machineId ?? undefined,
        sinceMinutes: filters.sinceMinutes,
        q: debouncedQ || undefined,
        includeArchived: filters.includeArchived,
        feedback: filters.feedback,
        feedbackReason: filters.feedbackReason,
        failures: filters.failures,
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...res.conversations]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }, [
    token,
    nextCursor,
    filters.customerId,
    filters.machineId,
    filters.sinceMinutes,
    filters.includeArchived,
    filters.feedback,
    filters.feedbackReason,
    filters.failures,
    debouncedQ,
  ]);

  const machineOptions = useMemo<AdminConversationFacetMachine[]>(() => {
    if (!facets) return [];
    if (!filters.customerId) return facets.machines;
    return facets.machines.filter((m) => m.customerId === filters.customerId);
  }, [facets, filters.customerId]);

  const activeFilterCount = countActiveFilters(filters);

  // Failures get a red rail on the row rather than a column of their own: the
  // table already carries twelve columns of tokens and cost, and a thirteenth
  // tipped it into horizontal scroll. The breakdown rides along as the row's
  // tooltip; tool-vs-turn stays reachable through the chips above and the
  // conversation detail. Returns null when nothing failed.
  const failureHint = (row: AdminConversationRow): string | null => {
    const { toolFailureCount, failedTurnCount, toolNames, turnErrorCodes } = row.failures;
    if (toolFailureCount === 0 && failedTurnCount === 0) return null;
    return [
      toolFailureCount > 0 ? t`${toolFailureCount} failed call(s): ${toolNames.join(", ")}` : null,
      failedTurnCount > 0
        ? turnErrorCodes.length > 0
          ? t`${failedTurnCount} failed turn(s): ${turnErrorCodes.join(", ")}`
          : t`${failedTurnCount} failed turn(s), without an error code`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.cost += r.costUsd;
        acc.input += r.tokens.inputTokens;
        acc.cached += r.tokens.cachedInputTokens;
        acc.cacheWrite += r.tokens.cacheWriteTokens;
        acc.output += r.tokens.outputTokens;
        return acc;
      },
      { cost: 0, input: 0, cached: 0, cacheWrite: 0, output: 0 },
    );
  }, [rows]);

  const currentTimeLabel =
    TIME_PRESETS.find((p) => p.minutes === filters.sinceMinutes)?.label ??
    `${filters.sinceMinutes}m`;

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Conversations</Trans></h1>
        <div className="row-actions">
          <button
            className="ghost"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            disabled={activeFilterCount === 0}
            title={t`Clear all filters`}
          >
            <Trans>Reset</Trans>{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <button className="ghost" onClick={() => void load()} disabled={loading}>
            {loading ? <Trans>Refreshing…</Trans> : <Trans>Refresh</Trans>}
          </button>
        </div>
      </header>

      {loadError && <div className="banner err">{loadError}</div>}

      <section className="errors-filters">
        <div className="errors-search">
          <span className="errors-search-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={filters.q}
            placeholder={t`Search by title…`}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          {filters.q && (
            <button
              className="errors-search-clear"
              onClick={() => setFilters((f) => ({ ...f, q: "" }))}
              aria-label={t`Clear search`}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div className="errors-filter-grid">
          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>Time range</Trans></span>
            <div className="chips">
              {TIME_PRESETS.map((p) => (
                <button
                  key={p.minutes}
                  className={filters.sinceMinutes === p.minutes ? "chip active" : "chip"}
                  onClick={() => setFilters((f) => ({ ...f, sinceMinutes: p.minutes }))}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="errors-filter-select">
            <span className="errors-filter-label"><Trans>Customer</Trans></span>
            <select
              value={filters.customerId ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  customerId: e.target.value || null,
                  machineId: null,
                }))
              }
            >
              <option value="">{t`All customers`}</option>
              {facets?.customers.map((c) => (
                <option key={c.customerId} value={c.customerId}>
                  {c.customerName} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="errors-filter-select">
            <span className="errors-filter-label"><Trans>Machine</Trans></span>
            <select
              value={filters.machineId ?? ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, machineId: e.target.value || null }))
              }
              disabled={machineOptions.length === 0}
            >
              <option value="">{t`All machines`}</option>
              {machineOptions.map((m) => (
                <option key={m.machineId} value={m.machineId}>
                  {m.machineName ?? m.machineId.slice(0, 8) + "…"} — {m.customerName} ({m.count})
                </option>
              ))}
            </select>
          </div>

          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>Failures</Trans></span>
            <div className="chips">
              {FAILURE_PRESETS.map((p) => {
                const count =
                  p.value === "any"
                    ? facets?.failureCounts.any
                    : p.value === "tool"
                      ? facets?.failureCounts.tool
                      : p.value === "turn"
                        ? facets?.failureCounts.turn
                        : undefined;
                return (
                  <button
                    key={p.value ?? "all"}
                    className={filters.failures === p.value ? "chip active" : "chip"}
                    onClick={() => setFilters((f) => ({ ...f, failures: p.value }))}
                  >
                    {i18n._(p.label)}
                    {count ? ` (${count})` : ""}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>User feedback</Trans></span>
            <div className="chips">
              {FEEDBACK_PRESETS.map((p) => (
                <button
                  key={p.value ?? "all"}
                  className={filters.feedback === p.value ? "chip active" : "chip"}
                  // Picking anything other than "flagged" drops the reason —
                  // a reason only narrows a flagged set.
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      feedback: p.value,
                      feedbackReason: p.value === "flagged" ? f.feedbackReason : null,
                    }))
                  }
                >
                  {i18n._(p.label)}
                  {p.value === "flagged" && facets && facets.flaggedCount > 0
                    ? ` (${facets.flaggedCount})`
                    : ""}
                </button>
              ))}
            </div>
          </div>

          <div className="errors-filter-select">
            <span className="errors-filter-label"><Trans>Reported reason</Trans></span>
            <select
              value={filters.feedbackReason ?? ""}
              // Selecting a reason implies the flagged filter, so the chips stay
              // consistent with what the list actually shows.
              onChange={(e) =>
                setFilters((f) => {
                  const reason = (e.target.value || null) as MessageFeedbackReason | null;
                  return {
                    ...f,
                    feedbackReason: reason,
                    feedback: reason ? "flagged" : f.feedback,
                  };
                })
              }
            >
              <option value="">{t`All reasons`}</option>
              {MESSAGE_FEEDBACK_REASONS.map((r) => {
                const count = facets?.feedbackReasons.find((x) => x.reason === r)?.count ?? 0;
                return (
                  <option key={r} value={r}>
                    {i18n._(FEEDBACK_REASON_LABELS[r])} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>Archived</Trans></span>
            <label className="conversations-toggle">
              <input
                type="checkbox"
                checked={filters.includeArchived}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, includeArchived: e.target.checked }))
                }
              />
              <span><Trans>Include archived</Trans></span>
            </label>
          </div>
        </div>
      </section>

      <div className="errors-summary">
        <div className="errors-result-count">
          {loading ? (
            <span className="muted"><Trans>Loading…</Trans></span>
          ) : (
            <>
              <strong>{rows.length}</strong>
              <span className="muted">
                {" "}<Trans>conversation(s)</Trans>{nextCursor ? "+" : ""} ·{" "}
                <Trans>last {currentTimeLabel}</Trans>
              </span>
              {rows.length > 0 && (
                <span className="muted">
                  {" · "}
                  <Trans>total {formatCost(totals.cost, i18n.locale)}</Trans>
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <section>
        {loading ? (
          <p className="muted"><Trans>Loading…</Trans></p>
        ) : rows.length === 0 ? (
          <div className="errors-empty">
            <p className="muted"><Trans>No conversation matches these filters.</Trans></p>
            {activeFilterCount > 0 && (
              <button className="link-btn" onClick={() => setFilters(DEFAULT_FILTERS)}>
                <Trans>Clear filters</Trans>
              </button>
            )}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th><Trans>Updated</Trans></th>
                  <th><Trans>Title</Trans></th>
                  <th><Trans>Customer</Trans></th>
                  <th><Trans>Machine</Trans></th>
                  <th><Trans>Feedback</Trans></th>
                  <th className="num"><Trans>Msgs</Trans></th>
                  <th className="num"><Trans>Turns</Trans></th>
                  <th className="num"><Trans>Input</Trans></th>
                  <th className="num"><Trans>Cached</Trans></th>
                  <th className="num"><Trans>Cache-w</Trans></th>
                  <th className="num"><Trans>Output</Trans></th>
                  <th className="num"><Trans>Cost</Trans></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const failure = failureHint(r);
                  return (
                    <tr
                      key={r.id}
                      className={failure ? "row-clickable conversation-failed" : "row-clickable"}
                      title={failure ?? undefined}
                    >
                      <td className="mono">
                        <Link to={`/conversations/${r.id}`}>
                          {new Date(r.updatedAt).toLocaleString(i18n.locale)}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/conversations/${r.id}`}>{r.title || t`(untitled)`}</Link>
                        {r.archivedAt && (
                          <span className="muted"> · <Trans>archived</Trans></span>
                        )}
                        {r.credit && (
                          <>
                            {" "}
                            <Badge tone="ok">
                              {i18n._(CREDIT_REASON_LABELS[r.credit.reason])}
                            </Badge>
                          </>
                        )}
                      </td>
                      <td>
                        <Link to={`/customers/${r.customerId}`}>{r.customerName}</Link>
                      </td>
                      <td>{r.machineName ?? (r.machineId ? r.machineId.slice(0, 8) + "…" : "—")}</td>
                      <td><FeedbackCell row={r} /></td>
                      <td className="num mono">{formatInt(r.messageCount, i18n.locale)}</td>
                      <td className="num mono">{formatInt(r.turnCount, i18n.locale)}</td>
                      <td className="num mono">{formatInt(r.tokens.inputTokens, i18n.locale)}</td>
                      <td className="num mono">{formatInt(r.tokens.cachedInputTokens, i18n.locale)}</td>
                      <td className="num mono">{formatInt(r.tokens.cacheWriteTokens, i18n.locale)}</td>
                      <td className="num mono">{formatInt(r.tokens.outputTokens, i18n.locale)}</td>
                      <td className="num mono">
                        {r.credit ? (
                          <s className="muted">{formatCost(r.costUsd, i18n.locale)}</s>
                        ) : (
                          formatCost(r.costUsd, i18n.locale)
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {nextCursor && (
              <div className="load-more">
                <button className="ghost" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? <Trans>Loading…</Trans> : <Trans>Load more</Trans>}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// Compact summary of a conversation's ratings: the flag (with its reasons, so
// the list answers "why" without a click) takes precedence over thumbs-up,
// which is shown only when nothing was flagged.
function FeedbackCell({ row }: { row: AdminConversationRow }) {
  const { t, i18n } = useLingui();
  const { negativeCount, positiveCount, reasons } = row.feedback;
  if (negativeCount === 0 && positiveCount === 0) {
    return <span className="muted">—</span>;
  }
  if (negativeCount === 0) {
    return <Badge tone="ok">{`👍 ${positiveCount}`}</Badge>;
  }
  return (
    <span
      className="conversation-feedback-cell"
      title={
        reasons.length > 0
          ? reasons.map((r) => i18n._(FEEDBACK_REASON_LABELS[r])).join(" · ")
          : t`Reported without a reason`
      }
    >
      <Badge tone="err">{`👎 ${negativeCount}`}</Badge>
      {reasons.length > 0 && (
        <span className="muted conversation-feedback-reasons">
          {reasons.map((r) => i18n._(FEEDBACK_REASON_LABELS[r])).join(", ")}
        </span>
      )}
    </span>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function countActiveFilters(f: Filters): number {
  let n = 0;
  if (f.customerId) n += 1;
  if (f.machineId) n += 1;
  if (f.q.trim().length > 0) n += 1;
  if (f.sinceMinutes !== DEFAULT_FILTERS.sinceMinutes) n += 1;
  if (f.includeArchived) n += 1;
  if (f.feedback) n += 1;
  if (f.feedbackReason) n += 1;
  if (f.failures) n += 1;
  return n;
}

function parseFeedbackFilter(v: string | null): AdminConversationFeedbackFilter | null {
  return v === "flagged" || v === "unflagged" || v === "rated" ? v : null;
}

function parseFailureFilter(v: string | null): AdminConversationFailureFilter | null {
  return v === "any" || v === "tool" || v === "turn" || v === "clean" ? v : null;
}

function parseFeedbackReason(v: string | null): MessageFeedbackReason | null {
  return (MESSAGE_FEEDBACK_REASONS as readonly string[]).includes(v ?? "")
    ? (v as MessageFeedbackReason)
    : null;
}

function formatInt(n: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(Math.round(n));
}

export function formatCost(n: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(n);
}

function filtersFromParams(params: URLSearchParams): Filters {
  const since = Number(params.get("sinceMinutes") ?? String(DEFAULT_FILTERS.sinceMinutes));
  return {
    customerId: params.get("customerId"),
    machineId: params.get("machineId"),
    sinceMinutes: Number.isFinite(since) && since > 0 ? since : DEFAULT_FILTERS.sinceMinutes,
    q: params.get("q") ?? "",
    includeArchived: params.get("includeArchived") === "true",
    feedback: parseFeedbackFilter(params.get("feedback")),
    feedbackReason: parseFeedbackReason(params.get("feedbackReason")),
    failures: parseFailureFilter(params.get("failures")),
  };
}

function paramsFromFilters(f: Filters): URLSearchParams {
  const params = new URLSearchParams();
  if (f.customerId) params.set("customerId", f.customerId);
  if (f.machineId) params.set("machineId", f.machineId);
  params.set("sinceMinutes", String(f.sinceMinutes));
  if (f.q) params.set("q", f.q);
  if (f.includeArchived) params.set("includeArchived", "true");
  if (f.feedback) params.set("feedback", f.feedback);
  if (f.feedbackReason) params.set("feedbackReason", f.feedbackReason);
  if (f.failures) params.set("failures", f.failures);
  return params;
}
